<template>
  <div class="relative w-full">
    <!-- Hidden File Input -->
    <input
      ref="fileInputRef"
      type="file"
      class="hidden"
      :accept="accept"
      @change="handleFileChange"
    >

    <!-- Empty Upload State -->
    <div
      v-if="!hasImage"
      class="border-2 border-dashed border-[#c1cfe1] hover:border-[#2f6fab] bg-[#f8fbff] hover:bg-[#f2f7fd] rounded-[1.5rem] p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center w-full group shadow-2xs"
      :class="isBanner ? 'aspect-square' : 'min-h-[148px]'"
      @click="triggerUpload"
    >
      <div class="text-[#2f6fab] flex flex-col items-center select-none pointer-events-none group-hover:scale-105 transition-transform">
        <div class="w-12 h-12 rounded-full bg-[#eaf3fb] flex items-center justify-center mb-2.5 shadow-2xs">
          <svg
            class="w-6 h-6 text-[#2855a6]"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <span class="text-xs font-black text-[#173b5f]">{{ label }}</span>
        <span class="text-[10px] text-[#748197] mt-1">{{ hint }}</span>
      </div>
    </div>

    <!-- Active Canvas Container -->
    <div
      v-else
      class="relative border border-[#d8e4f0] bg-[#0c121e] rounded-[1.5rem] overflow-hidden flex items-center justify-center transition-all w-full"
      :class="isBanner ? 'aspect-square' : ''"
      :style="isBanner ? {} : { minHeight: props.height || '148px' }"
    >
      <!-- Viewport Mask (Fixed Aspect Ratio Frame) -->
      <div
        ref="viewportRef"
        class="relative w-full h-full overflow-hidden flex items-center justify-center select-none touch-none rounded-[1.5rem]"
        :style="viewportStyle"
        :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
        @mousedown="onMouseDown"
        @touchstart.passive="onTouchStart"
        @wheel.prevent="onWheel"
      >
        <!-- The Transformed Image (Visible inside viewport) -->
        <img
          v-if="rawSrc"
          ref="imageRef"
          :src="rawSrc"
          alt="Canvas Image"
          crossorigin="anonymous"
          class="absolute max-w-none pointer-events-none transition-transform duration-75 ease-out origin-center"
          :style="imageTransformStyle"
          @load="onImageLoaded"
        >

        <!-- Rule-of-Thirds Grid Guides (Only active when dragging) -->
        <div
          v-if="isDragging && imageLoaded"
          class="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none opacity-30"
        >
          <div class="border-r border-b border-white" />
          <div class="border-r border-b border-white" />
          <div class="border-b border-white" />
          <div class="border-r border-b border-white" />
          <div class="border-r border-b border-white" />
          <div class="border-b border-white" />
          <div class="border-r border-b border-white" />
          <div class="border-r border-b border-white" />
          <div />
        </div>

        <!-- Subtle Guidance Badge (Non-obstructive) -->
        <div
          v-if="imageLoaded"
          class="absolute top-2.5 left-2.5 pointer-events-none bg-slate-900/75 backdrop-blur-xs text-[9px] font-bold text-white px-2.5 py-0.5 rounded-full border border-white/20 flex items-center gap-1.5 shadow-xs"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse" />
          <span>Drag to Pan &bull; Ctrl+Scroll to Zoom</span>
        </div>
      </div>

      <!-- Top-Right Action Pill Buttons -->
      <div
        v-if="hasImage && imageLoaded"
        class="absolute top-2.5 right-2.5 flex items-center gap-1.5 z-10"
      >
        <span
          v-if="isExporting"
          class="bg-slate-900/80 text-white text-[9.5px] px-2 py-0.5 rounded-md font-semibold border border-white/20"
        >
          Saving...
        </span>

        <button
          type="button"
          class="inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[#173b5f] px-2 py-1 rounded-md text-[11px] font-bold shadow-md transition-all active:scale-95 cursor-pointer"
          title="Change Image"
          @click.stop="triggerUpload"
        >
          <svg
            class="w-3.5 h-3.5 text-[#2855a6]"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <span>Change</span>
        </button>

        <button
          type="button"
          class="bg-[#be243d] hover:bg-[#9c182d] text-white p-1 rounded-md text-[11px] shadow-md transition-all active:scale-95 cursor-pointer"
          title="Remove Image"
          @click.stop="removeImage"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: File | null;
    existingUrl?: string;
    aspectRatio?: number; // 1 for logo, 16/9 for banner
    isBanner?: boolean;
    height?: string;
    label?: string;
    hint?: string;
    accept?: string;
    maxSizeMB?: number;
    targetWidth?: number;
    targetHeight?: number;
    fileName?: string;
  }>(),
  {
    existingUrl: "",
    aspectRatio: 1,
    isBanner: false,
    height: "",
    label: "Select Image",
    hint: "PNG, JPG, WEBP",
    accept: ".png,.jpg,.jpeg,.webp",
    maxSizeMB: 5,
    targetWidth: 800,
    targetHeight: 800,
    fileName: "cropped-image.png",
  }
);

const emit = defineEmits<{
  (event: "update:modelValue", file: File | null): void;
  (event: "change", file: File | null): void;
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);
const viewportRef = ref<HTMLDivElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);

const rawSrc = ref("");
const isEditing = ref(false);
const isExporting = ref(false);
const imageLoaded = ref(false);

const naturalWidth = ref(0);
const naturalHeight = ref(0);

// Crop Viewport Dimensions (measured in pixels)
const viewportWidth = ref(200);
const viewportHeight = ref(112);

// User Transforms
const userZoom = ref(1.0);
const offsetX = ref(0);
const offsetY = ref(0);

// Drag State
const isDragging = ref(false);
const dragStart = { x: 0, y: 0, initialOffsetX: 0, initialOffsetY: 0 };

const hasImage = computed(() => Boolean(rawSrc.value || props.modelValue || props.existingUrl));

// Style for Viewport Boundary
const viewportStyle = computed(() => {
  if (props.isBanner) {
    return {
      width: "100%",
      height: "100%",
      borderRadius: "1.5rem",
    };
  }
  // Square Logo: centered 132x132px frame
  return {
    width: "132px",
    height: "132px",
    borderRadius: "0.5rem",
  };
});

// Base scale needed so image completely fills the viewport
const baseScale = computed(() => {
  if (!naturalWidth.value || !naturalHeight.value || !viewportWidth.value || !viewportHeight.value) {
    return 1;
  }
  return Math.max(
    viewportWidth.value / naturalWidth.value,
    viewportHeight.value / naturalHeight.value
  );
});

const currentScale = computed(() => baseScale.value * userZoom.value);
const renderedWidth = computed(() => naturalWidth.value * currentScale.value);
const renderedHeight = computed(() => naturalHeight.value * currentScale.value);

function clampOffsets(x: number, y: number) {
  const maxOffsetX = Math.max(0, (renderedWidth.value - viewportWidth.value) / 2);
  const maxOffsetY = Math.max(0, (renderedHeight.value - viewportHeight.value) / 2);
  return {
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, x)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, y)),
  };
}

const imageTransformStyle = computed(() => {
  const cx = viewportWidth.value / 2;
  const cy = viewportHeight.value / 2;
  const left = cx - renderedWidth.value / 2 + offsetX.value;
  const top = cy - renderedHeight.value / 2 + offsetY.value;

  return {
    width: `${renderedWidth.value}px`,
    height: `${renderedHeight.value}px`,
    left: `${left}px`,
    top: `${top}px`,
  };
});

function measureViewport() {
  if (!viewportRef.value) return;
  viewportWidth.value = viewportRef.value.clientWidth || (props.isBanner ? 380 : 132);
  viewportHeight.value = viewportRef.value.clientHeight || (props.isBanner ? 380 : 132);

  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function triggerUpload() {
  fileInputRef.value?.click();
}

function handleFileChange(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files?.[0]) {
    const file = target.files[0];
    if (file.size > props.maxSizeMB * 1024 * 1024) {
      alert(`File exceeds the ${props.maxSizeMB}MB size limit.`);
      target.value = "";
      return;
    }

    if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
      URL.revokeObjectURL(rawSrc.value);
    }

    rawSrc.value = URL.createObjectURL(file);
    imageLoaded.value = false;
    userZoom.value = 1.0;
    offsetX.value = 0;
    offsetY.value = 0;
    isEditing.value = true; // Automatically enters edit mode on upload!

    target.value = "";
  }
}

function onImageLoaded(e: Event) {
  const img = e.target as HTMLImageElement;
  naturalWidth.value = img.naturalWidth;
  naturalHeight.value = img.naturalHeight;
  imageLoaded.value = true;
  measureViewport();
  scheduleAutoSave();
}

function startEditing() {
  measureViewport();
}

function zoomStep(delta: number) {
  userZoom.value = Math.max(1.0, Math.min(3.5, +(userZoom.value + delta).toFixed(2)));
  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onWheel(e: WheelEvent) {
  if (!imageLoaded.value) return;
  const delta = e.deltaY < 0 ? 0.08 : -0.08;
  zoomStep(delta);
  scheduleAutoSave();
}

// Mouse dragging
function onMouseDown(e: MouseEvent) {
  if (e.button !== 0 || !imageLoaded.value) return;
  isDragging.value = true;
  dragStart.x = e.clientX;
  dragStart.y = e.clientY;
  dragStart.initialOffsetX = offsetX.value;
  dragStart.initialOffsetY = offsetY.value;

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - dragStart.x;
  const dy = e.clientY - dragStart.y;
  const clamped = clampOffsets(dragStart.initialOffsetX + dx, dragStart.initialOffsetY + dy);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onMouseUp() {
  if (isDragging.value) {
    isDragging.value = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    scheduleAutoSave();
  }
}

// Touch dragging
function onTouchStart(e: TouchEvent) {
  if (e.touches.length !== 1 || !imageLoaded.value) return;
  isDragging.value = true;
  dragStart.x = e.touches[0].clientX;
  dragStart.y = e.touches[0].clientY;
  dragStart.initialOffsetX = offsetX.value;
  dragStart.initialOffsetY = offsetY.value;

  window.addEventListener("touchmove", onTouchMove);
  window.addEventListener("touchend", onTouchEnd);
}

function onTouchMove(e: TouchEvent) {
  if (!isDragging.value || e.touches.length !== 1) return;
  const dx = e.touches[0].clientX - dragStart.x;
  const dy = e.touches[0].clientY - dragStart.y;
  const clamped = clampOffsets(dragStart.initialOffsetX + dx, dragStart.initialOffsetY + dy);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onTouchEnd() {
  if (isDragging.value) {
    isDragging.value = false;
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", onTouchEnd);
    scheduleAutoSave();
  }
}

watch(userZoom, () => {
  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
});

let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleAutoSave() {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  autoSaveTimeout = setTimeout(() => {
    applyCrop();
  }, 180);
}

// Render high-res crop on offscreen canvas and emit result
async function applyCrop() {
  if (!imageRef.value || !imageLoaded.value) return;

  isExporting.value = true;
  try {
    const scale = currentScale.value;
    const cx = viewportWidth.value / 2;
    const cy = viewportHeight.value / 2;

    const imgLeft = cx - renderedWidth.value / 2 + offsetX.value;
    const imgTop = cy - renderedHeight.value / 2 + offsetY.value;

    const sx = Math.max(0, -imgLeft / scale);
    const sy = Math.max(0, -imgTop / scale);
    const sWidth = Math.min(naturalWidth.value - sx, viewportWidth.value / scale);
    const sHeight = Math.min(naturalHeight.value - sy, viewportHeight.value / scale);

    const canvas = document.createElement("canvas");
    canvas.width = props.targetWidth;
    canvas.height = props.targetHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) throw new Error("Canvas context failed");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      imageRef.value,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      props.targetWidth,
      props.targetHeight
    );

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png", 0.95);
    });

    if (blob) {
      const file = new File([blob], props.fileName, { type: "image/png" });
      emit("update:modelValue", file);
      emit("change", file);
    }
  } catch (err) {
    console.error("Failed to crop canvas image:", err);
  } finally {
    isExporting.value = false;
  }
}

function removeImage() {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
    URL.revokeObjectURL(rawSrc.value);
  }
  rawSrc.value = "";
  imageLoaded.value = false;
  isEditing.value = false;
  userZoom.value = 1.0;
  offsetX.value = 0;
  offsetY.value = 0;
  emit("update:modelValue", null);
  emit("change", null);
}

// Initialize from existingUrl if provided
watch(
  () => props.existingUrl,
  (newUrl) => {
    if (newUrl && !rawSrc.value && !props.modelValue) {
      rawSrc.value = newUrl;
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener("resize", measureViewport);
  setTimeout(measureViewport, 60);
});

onUnmounted(() => {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  window.removeEventListener("resize", measureViewport);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("touchmove", onTouchMove);
  window.removeEventListener("touchend", onTouchEnd);
  if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
    URL.revokeObjectURL(rawSrc.value);
  }
});

defineExpose({
  triggerUpload,
  removeImage,
  applyCrop,
  hasImage,
});
</script>
